import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';

import { cn } from '@/shared/lib/cn';
import { sheetOverlayTint } from '@/shared/ui/shadcn/modal-chrome';
import { useModalLayer } from '@/shared/hooks/use-modal-layer';

// Mirrors the Root `modal` prop for DrawerOverlay (see its comment). Vaul
// exposes no public way to read it from inside the portal subtree.
const DrawerModalContext = React.createContext(true);

const Drawer = ({
  shouldScaleBackground = true,
  modal = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerModalContext.Provider value={modal}>
    <DrawerPrimitive.Root
      shouldScaleBackground={shouldScaleBackground}
      modal={modal}
      {...props}
    />
  </DrawerModalContext.Provider>
);
Drawer.displayName = 'Drawer';

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  // Upstream bug workaround (vaul 1.1.2, latest): vaul's Overlay component
  // early-returns `null` when `modal` is false BEFORE calling its
  // `useCallback` — a conditional-hook violation. Our Paddle paywalls flip
  // the Drawer's `modal` prop live (`modal={!checkoutActive}`) so the
  // body-level Paddle iframe stays clickable; on that flip vaul's Overlay
  // re-renders with fewer hooks and React crashes the whole tree with
  // minified error #300 ("Rendered fewer hooks than expected") — the
  // ErrorBoundary "Something went wrong" over the checkout. Unmounting the
  // Overlay from OUT HERE instead (same DOM outcome: no overlay when
  // non-modal) means vaul's component never re-renders across the flip, so
  // the hook mismatch can't happen. Remove once vaul moves the hook above
  // its early return.
  const modal = React.useContext(DrawerModalContext);
  // Above the early return, and only while actually modal: a non-modal drawer
  // deliberately leaves the page (and Escape) alone.
  useModalLayer(modal);
  if (!modal) return null;
  return (
    <DrawerPrimitive.Overlay
      ref={ref}
      // z-[49] (one below the drawer content's z-50), NOT z-50: Radix Portal
      // mounts overlay and content as SEPARATE portals appended to <body>, so
      // when this overlay remounts after a Paddle `modal` flip (see above) its
      // portal lands AFTER the content's — with equal z-index the later DOM
      // node wins and the dark overlay paints on top of the sheet. The
      // explicit lower z keeps the overlay behind the sheet regardless of
      // mount order.
      className={cn('fixed inset-0 z-[49]', sheetOverlayTint, className)}
      {...props}
    />
  );
});
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn(
        'fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto flex-col rounded-t-[10px] bg-background',
        className
      )}
      aria-describedby={undefined}
      {...props}
    >
      <div className="mx-auto mt-2.5 mb-1 h-1 w-10 rounded-full bg-muted-foreground/30" />
      {children}
    </DrawerPrimitive.Content>
  </DrawerPortal>
));

// Full-screen drawer content for render settings with responsive sizing
const DrawerContentFullScreen = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Set fixed height and listen to viewport changes
  React.useEffect(() => {
    if (!contentRef.current) return;

    // Set initial fixed height based on viewport
    const setFixedHeight = () => {
      if (!contentRef.current) return;

      const vw = window.innerWidth;
      let height: string;

      if (vw < 768) {
        // Mobile: full viewport height
        height = window.visualViewport
          ? `${window.visualViewport.height}px`
          : `${window.innerHeight}px`;
      } else if (vw < 1024) {
        // Tablet: 90vh
        height = `${window.innerHeight * 0.9}px`;
      } else {
        // Desktop: 85vh with max 900px
        height = `${Math.min(window.innerHeight * 0.85, 900)}px`;
      }

      // Set height with !important via inline style
      contentRef.current.style.height = height;
      contentRef.current.style.minHeight = height;
      contentRef.current.style.maxHeight = height;
    };

    // Set initial height
    setFixedHeight();

    // Update on viewport resize (keyboard open/close)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', setFixedHeight);
    }

    // Update on window resize (orientation change)
    window.addEventListener('resize', setFixedHeight);

    return () => {
      window.visualViewport?.removeEventListener('resize', setFixedHeight);
      window.removeEventListener('resize', setFixedHeight);
    };
  }, []);

  // Merge refs
  React.useImperativeHandle(ref, () => contentRef.current!);

  return (
    <DrawerPortal>
      <DrawerOverlay />
      <DrawerPrimitive.Content
        ref={contentRef}
        className={cn(
          // Base styles - always anchored to bottom with FIXED height
          'fixed bottom-0 z-50 flex flex-col bg-background shadow-xl',
          // Mobile: full width, full viewport height (fixed, not auto)
          'inset-x-0 !h-[calc(100dvh-0px)] rounded-t-[10px]',
          // Tablet: 90% height (fixed)
          'md:!h-[90dvh]',
          // Desktop: centered horizontally, anchored to bottom with optimized height
          'lg:left-1/2 lg:-translate-x-1/2',
          'lg:!h-[85dvh] lg:max-h-[900px]',
          // Width: narrower on desktop with proper padding
          'lg:w-[480px] lg:px-4',
          // Extra large screens: slightly taller but maintain narrower width
          'xl:!h-[min(85dvh,950px)]',
          'xl:w-[520px] xl:px-5',
          // Ultra-wide screens: maintain reasonable height
          '2xl:!h-[min(80dvh,1000px)]',
          '2xl:w-[560px] 2xl:px-6',
          // Keep rounded corners only on top
          'lg:rounded-t-[10px]',
          // Prevent scroll behavior issues on mobile
          'overscroll-contain',
          // Animation
          'transition-all duration-300 ease-in-out',
          className
        )}
        aria-describedby={undefined}
        {...props}
      >
        {/* Content area - prevent drag on this area, takes all remaining space */}
        {/* relative is needed for NestedSheet positioning */}
        <div
          className="flex-1 flex flex-col overflow-hidden relative"
          data-vaul-no-drag
        >
          {children}
        </div>
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
});
DrawerContent.displayName = 'DrawerContent';
DrawerContentFullScreen.displayName = 'DrawerContentFullScreen';

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('grid gap-1.5 px-4 pt-3 pb-1 text-start', className)}
    {...props}
  />
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('mt-auto flex flex-col gap-2 p-4', className)}
    {...props}
  />
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      'text-sheet-title leading-none tracking-tight',
      className
    )}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-form-help text-muted-foreground', className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerContentFullScreen,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
